/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */



import {Injectable, Optional} from '../di';

import {Compiler} from './compiler';
import {NgModuleFactory} from './ng_module_factory';
import {NgModuleFactoryLoader} from './ng_module_factory_loader';

const /** @type {?} */ _SEPARATOR = '#';

const /** @type {?} */ FACTORY_CLASS_SUFFIX = 'NgFactory';
/**
 * Configuration for SystemJsNgModuleLoader.
 * token.
 * 
 * \@experimental
 * @abstract
 */
export abstract class SystemJsNgModuleLoaderConfig {
  /**
   * Prefix to add when computing the name of the factory module for a given module name.
   */
  factoryPathPrefix: string;

  /**
   * Suffix to add when computing the name of the factory module for a given module name.
   */
  factoryPathSuffix: string;
}

function SystemJsNgModuleLoaderConfig_tsickle_Closure_declarations() {
/**
 * Prefix to add when computing the name of the factory module for a given module name.
 * @type {?}
 */
SystemJsNgModuleLoaderConfig.prototype.factoryPathPrefix;
/**
 * Suffix to add when computing the name of the factory module for a given module name.
 * @type {?}
 */
SystemJsNgModuleLoaderConfig.prototype.factoryPathSuffix;
}


const /** @type {?} */ DEFAULT_CONFIG: SystemJsNgModuleLoaderConfig = {
  factoryPathPrefix: '',
  factoryPathSuffix: '.ngfactory',
};
/**
 * NgModuleFactoryLoader that uses SystemJS to load NgModuleFactory
 * \@experimental
 */
export class SystemJsNgModuleLoader implements NgModuleFactoryLoader {
private _config: SystemJsNgModuleLoaderConfig;
/**
 * @param {?} _compiler
 * @param {?=} config
 */
constructor(private _compiler: Compiler,  config?: SystemJsNgModuleLoaderConfig) {
    this._config = config || DEFAULT_CONFIG;
  }
/**
 * @param {?} path
 * @return {?}
 */
load(path: string): Promise<NgModuleFactory<any>> {
    const /** @type {?} */ offlineMode = this._compiler instanceof Compiler;
    return offlineMode ? this.loadFactory(path) : this.loadAndCompile(path);
  }
/**
 * @param {?} path
 * @return {?}
 */
private loadAndCompile(path: string): Promise<NgModuleFactory<any>> {
    let [module, exportName] = path.split(_SEPARATOR);
    if (exportName === undefined) {
      exportName = 'default';
    }

    return System.import(module)
        .then((module: any) => module[exportName])
        .then((type: any) => checkNotEmpty(type, module, exportName))
        .then((type: any) => this._compiler.compileModuleAsync(type));
  }
/**
 * @param {?} path
 * @return {?}
 */
private loadFactory(path: string): Promise<NgModuleFactory<any>> {
    let [module, exportName] = path.split(_SEPARATOR);
    let /** @type {?} */ factoryClassSuffix = FACTORY_CLASS_SUFFIX;
    if (exportName === undefined) {
      exportName = 'default';
      factoryClassSuffix = '';
    }

    return System.import(this._config.factoryPathPrefix + module + this._config.factoryPathSuffix)
        .then((module: any) => module[exportName + factoryClassSuffix])
        .then((factory: any) => checkNotEmpty(factory, module, exportName));
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Compiler, },
{type: SystemJsNgModuleLoaderConfig, decorators: [{ type: Optional }, ]},
];
}

function SystemJsNgModuleLoader_tsickle_Closure_declarations() {
/** @type {?} */
SystemJsNgModuleLoader.decorators;
/**
 * @nocollapse
 * @type {?}
 */
SystemJsNgModuleLoader.ctorParameters;
/** @type {?} */
SystemJsNgModuleLoader.prototype._config;
/** @type {?} */
SystemJsNgModuleLoader.prototype._compiler;
}

/**
 * @param {?} value
 * @param {?} modulePath
 * @param {?} exportName
 * @return {?}
 */
function checkNotEmpty(value: any, modulePath: string, exportName: string): any {
  if (!value) {
    throw new Error(`Cannot find '${exportName}' in '${modulePath}'`);
  }
  return value;
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
